import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe, CommonModule, NgForOf, NgIf} from "@angular/common";
import {
    FormControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup
} from "@angular/forms";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {MatError, MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatIcon, MatIconModule} from "@angular/material/icon";
import {MatButtonModule, MatIconButton} from "@angular/material/button";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatTooltip, MatTooltipModule} from "@angular/material/tooltip";
import {CityTypes} from "../../../../auth/auth.types";
import {catchError, finalize, Observable, of, Subject, takeUntil} from "rxjs";
import {MatPaginatorModule} from "@angular/material/paginator";
import {RestFactoryService} from "../../../../../core/services/rest-factory.service";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatMenuModule} from "@angular/material/menu";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatRippleModule} from "@angular/material/core";
import {MatSortModule} from "@angular/material/sort";
import {MatSelectModule} from "@angular/material/select";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatDialogModule} from "@angular/material/dialog";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {PhoneInputComponent} from "../../../../components/phone-input/phone-input.component";
import {MatCardModule} from "@angular/material/card";
import {MatListModule} from "@angular/material/list";
import {MatDividerModule} from "@angular/material/divider";

@Component({
    selector: 'app-destination-client-manager',
    standalone: true,
    imports: [
        AsyncPipe,
        FormsModule,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatError,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatLabel,
        MatOption,
        MatTooltip,
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatRippleModule,
        MatSortModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatToolbarModule,
        MatDialogModule,
        MatDatepickerModule,
        NgIf,
        ReactiveFormsModule,
        NgForOf,
        PhoneInputComponent,
        MatCardModule,
        MatListModule,
        MatDividerModule,
    ],
    templateUrl: './destination-client-manager.component.html',
    styleUrl: './destination-client-manager.component.scss'
})
export class DestinationClientManagerComponent implements OnInit, OnDestroy {

    @Input() clientData: any = null;
    @Input() managersList: any[] = [];
    @Input() id_destination: string = null;

    isMobile: boolean;
    selectedStoryTypeForm: UntypedFormGroup;
    inputInternalSearchControl: UntypedFormControl = new UntypedFormControl("");
    originalMapList: any[] = [];
    filteredMapList: any[] = [];
    selectedMapElement: any = null;
    parentArray: CityTypes[] = [];
    parentControl = new FormControl(null);
    filteredParents: Observable<any[]>;
    parentCheckerRequired: boolean = true;
    typingTimeout: any;
    destination: CityTypes = null;
    inputSearchControl: UntypedFormControl = new UntypedFormControl("");
    disableAutoSave: boolean = true;

    private API_GET_DESTINATIONS_SEARCH = "/admin/destination/managersearch";
    private API_ADD_TO_MAP = "/admin/destination/manageradd";
    private API_DELETE_FROM_MAP = "/admin/destination/managerdelete";
    private API_UPDATE_CLIENT = "/admin/destination/clientupdate";

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _formBuilder: UntypedFormBuilder,
        private _restFactoryService: RestFactoryService,
        private breakpointObserver: BreakpointObserver,
    ) {
    }

    ngOnInit(): void {
        this.breakpointObserver.observe([
            Breakpoints.HandsetPortrait,
            Breakpoints.HandsetLandscape
        ]).subscribe(result => {
            this.isMobile = result.matches;
        });
        this.dataInit();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    dataInit() {
        if (!this.clientData) {
            this.clientData = {
                ds_address1: null,
                ds_client: null,
                ds_commonname: null,
                ds_email: null,
                ds_legalname: null,
                ds_phone: null,
                ds_website: null,
                id_client: null,
            }
        }
        this.selectedStoryTypeForm = this._formBuilder.group({
            ds_address1: [this.clientData.ds_address1],
            ds_client: [this.clientData.ds_client],
            ds_commonname: [this.clientData.ds_commonname],
            ds_email: [this.clientData.ds_email],
            ds_legalname: [this.clientData.ds_legalname],
            ds_phone: [this.clientData.ds_phone],
            ds_website: [this.clientData.ds_website],
            id_client: [this.clientData.id_client]
        });

        this.originalMapList = this.managersList;
        this.filteredMapList = [...this.originalMapList];
        this.disableAutoSave = false;
    }

    addToMapList(): void {
        const data = {
            "id_user": this.selectedMapElement.id_user,
            "id_destination": this.id_destination
        }
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);
        this._unsubscribeAll.next(null);
        this._restFactoryService.httpPostService(this.API_ADD_TO_MAP, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    return err;
                })
            )
            .subscribe(
                () => {
                    this.originalMapList.push(
                        this.selectedMapElement
                    );
                    this.searchMapList();
                    this.parentControl.reset();
                    this.parentControl.markAsPristine();
                    this.parentControl.markAsUntouched();
                    this._changeDetectorRef.detectChanges();
                });
    }

    onBlur(): void {
        setTimeout(() => {
            const value = this.parentControl.value;
            if (typeof value === 'string' && !this.parentArray.find(city => city.ds_name === value)) {
                this.parentControl.reset();
                this.parentControl.setErrors({'invalid': true});
                this.parentControl.markAsTouched();
                this.selectedMapElement = null;
            }
            if (!value) {
                this.parentControl.reset();
                this.parentControl.setErrors({'invalid': true});
                this.parentControl.markAsTouched();
                this.selectedMapElement = null;
            }
        }, 500);
    }

    displayFn(display: any): string {
        return display ? display.ds_email + ' (' + display.ds_firstname + ')' : '';
    }

    optionSelected(event: any) {
        this.selectedMapElement = event.option.value;
    }

    onKeyupAutoSearch(event: any): void {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            if (event.target.value.length > 2) {
                this.searchParent(event.target.value);
            }
        }, 500);
    }

    searchParent(searchTerm: string = null): void {
        const data = {
            "ds_keywords": searchTerm ? searchTerm : this.parentControl?.value,
        }
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);
        this._unsubscribeAll.next(null);
        this._restFactoryService.httpPostService(this.API_GET_DESTINATIONS_SEARCH, payload, "ar_results")
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                }),
                catchError((err) => {
                    return err;
                })
            )
            .subscribe(
                (response) => {
                    const filteredResponse = response.filter(
                        (responseItem) =>
                            !this.originalMapList.some(
                                (originalItem) => originalItem.id_user === responseItem.id_user
                            )
                    );
                    this.filteredParents = of(filteredResponse);
                    if (response.length === 0) {
                        this.parentControl.setErrors({'invalid': true});
                        this.parentControl.markAsTouched();
                    }
                    this._changeDetectorRef.detectChanges();
                });
    }

    searchMapList(): void {
        if (this.inputInternalSearchControl && this.inputInternalSearchControl.getRawValue()) {
            this.filteredMapList = this.originalMapList.filter(
                (item) => item.ds_email.toLowerCase().includes(this.inputInternalSearchControl.getRawValue().toLowerCase())
            );
        } else {
            this.filteredMapList = [...this.originalMapList];
        }
        this._changeDetectorRef.detectChanges();
        return;
    }

    deleteTag(id: string, eleKey: string): void {
        const payload = new FormData();
        payload.append(eleKey, id);
        payload.append("id_destination", this.id_destination);
        this._restFactoryService.httpPostService(this.API_DELETE_FROM_MAP, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    return err;
                })
            )
            .subscribe(
                () => {
                    this.originalMapList = this.originalMapList.filter((item) => item[eleKey] !== id);
                    this.searchMapList();
                    this._changeDetectorRef.detectChanges();
                });
        return;
    }

    onKeyupAutosave(): void {
        if (this.disableAutoSave || !this.clientData?.id_client) {
            return;
        }
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.saveAndClose();
        }, 500);
    }

    saveAndClose(): void {
        const payload = new FormData();

        const updatedClientData = {
            "id_client": this.selectedStoryTypeForm.value.id_client,
            "ds_client": this.selectedStoryTypeForm.value.ds_client,
            "ds_legalname": this.selectedStoryTypeForm.value.ds_legalname,
            "ds_commonname": this.selectedStoryTypeForm.value.ds_commonname,
            "ds_email": this.selectedStoryTypeForm.value.ds_email,
            "ds_phone": this.selectedStoryTypeForm.value.ds_phone,
            "ds_address1": this.selectedStoryTypeForm.value.ds_address1,
            "ds_website": this.selectedStoryTypeForm.value.ds_website
        };

        payload.append("js_clientdata", JSON.stringify(updatedClientData));

        this._unsubscribeAll.next(null);
        this._restFactoryService.httpPostService(this.API_UPDATE_CLIENT, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this._changeDetectorRef.detectChanges();
                }),
                catchError((err) => {
                    return err;
                })
            )
            .subscribe();
    }
}
